import { conversion } from 'country-configuration/currency'
import type {
	AccountBalance,
	BankData,
	BankDataRequest,
	MoneyCash,
	MoneyCashRequest,
	RefundData,
	RefundDataRequest,
} from 'refunds/types/refundData'
import { isUniqueMethod } from 'refunds/utils/isUniqueMethod'
import { PageType } from 'types/analytics'
import type { CountryConfiguration } from 'types/countryConfiguration'

export const normalizeBankData = (
	bankData: BankDataRequest,
	isUniqueMethodValue = false
): BankData => {
	const { bankDataInfo } = bankData
	if (!bankDataInfo) {
		return { ...bankData, bankDataInfo: null }
	}

	const { titular, nombre, apellidos, cuentaCompleta, numeroCuenta } =
		bankDataInfo

	return {
		...bankData,
		selected: bankData.available && (isUniqueMethodValue || bankData.selected),
		bankDataInfo: {
			...bankData.bankDataInfo,
			owner: titular ?? [nombre, apellidos].filter((v) => !!v).join(' '),
			bankAccount: cuentaCompleta ?? numeroCuenta,
		},
	}
}

export const normalizeMoneyCash = (
	moneyCash: MoneyCashRequest,
	isUniqueMethodValue = false
): MoneyCash => {
	const { moneyCashInfo } = moneyCash

	if (!moneyCashInfo) {
		return { ...moneyCash, moneyCashInfo: null }
	}

	const { firstName, middleName, lastName } = moneyCashInfo

	return {
		...moneyCash,
		selected: isUniqueMethodValue || moneyCash.selected,
		moneyCashInfo: {
			...moneyCash.moneyCashInfo,
			owner: [firstName, middleName, lastName].filter((v) => !!v).join(' '),
		},
	}
}

export const normalizeVisibilityProps = (
	pageType: PageType,
	refundData?: RefundData | null
) => {
	const { bankData, accountBalance, moneyCash } = refundData ?? {}
	const { available: isBankDataAvailable } = bankData ?? {}
	const { bankDataInfo } = bankData ?? {}
	const { moneyCashInfo } = moneyCash ?? {}

	const isUniqueMethodValue = isUniqueMethod(refundData)
	const showNoPurchaseMessage = !!(bankData && !isBankDataAvailable)
	const forceShowBankDataForm = !!(
		isUniqueMethodValue &&
		bankData &&
		!bankDataInfo
	)
	const forceShowMoneyCashForm = !!(
		isUniqueMethodValue &&
		moneyCash &&
		!moneyCashInfo
	)
	const isSaveButtonEnabled =
		pageType === PageType.Refunds &&
		!isUniqueMethodValue &&
		!showNoPurchaseMessage

	return {
		isAccountBalanceEnabled: !!accountBalance,
		isUniqueMethod: isUniqueMethodValue,
		showNoPurchaseMessage,
		forceShowBankDataForm,
		forceShowMoneyCashForm,
		isSaveButtonEnabled,
	}
}

export const normalizeRefundData = (
	data: RefundDataRequest,
	country: CountryConfiguration
): RefundData => {
	const { accountBalance, bankData, moneyCash } = data

	const price = accountBalance
		? conversion(accountBalance.balance, country).price
		: null

	const isUniqueMethodValue = isUniqueMethod(data)

	const normalizedAccountBalance: AccountBalance | null = accountBalance
		? {
				...accountBalance,
				selected: accountBalance.selected,
				balanceFormatted: price ?? '',
			}
		: null

	const normalizedBankData = bankData
		? normalizeBankData(bankData, isUniqueMethodValue)
		: null

	const normalizedMoneyCash = moneyCash
		? normalizeMoneyCash(moneyCash, isUniqueMethodValue)
		: null

	return {
		...data,
		accountBalance: normalizedAccountBalance,
		bankData: normalizedBankData,
		moneyCash: normalizedMoneyCash,
	}
}
